import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Landmark, LogOut, Loader2, Download } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { Navbar } from "@/components/Navbar";
import { Copilot } from "@/components/Copilot";
import { api, money, Badge, GateBadge, downloadBlob } from "@/pages/investor/api";

const TABS = ["Overview", "Access Requests", "Investors", "Vehicles", "Documents", "Statements", "Questions", "Gates & Settings", "Audit"];

export default function InvestorAdmin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("Overview");
  return (
    <div className="min-h-screen bg-ink" data-testid="investor-admin">
      <Navbar />
      <div className="border-b border-black/[0.08] bg-white/80 pt-[92px] backdrop-blur">
        <div className="container-x flex flex-wrap items-center justify-between gap-3 py-4">
          <div>
            <p className="eyebrow flex items-center gap-2 !text-red-700"><Landmark size={14} /> XE Admin · Investor Portal</p>
            <h1 className="font-display text-xl font-extrabold tracking-tight text-offwhite" data-testid="investor-admin-title">Investor Control Center</h1>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/admin" className="btn-ghost !px-4 !py-2 !text-xs" data-testid="back-to-xe-btn">Fleet Control Center</Link>
            <span className="hidden text-xs text-muted sm:block">{user?.email}</span>
            <button onClick={async () => { await logout(); navigate("/"); }} className="btn-ghost !px-4 !py-2 !text-xs" data-testid="investor-admin-signout-btn">
              <LogOut size={13} /> Sign out
            </button>
          </div>
        </div>
        <nav className="container-x flex gap-1 overflow-x-auto" aria-label="Investor admin">
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`ia-tab-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={`whitespace-nowrap border-b-2 px-4 py-3 text-sm font-semibold transition-colors ${tab === t ? "border-red-700 text-offwhite" : "border-transparent text-muted hover:text-offwhite"}`}>
              {t}
            </button>
          ))}
        </nav>
      </div>
      <main className="container-x py-8 pb-24">
        {tab === "Overview" && <OverviewTab />}
        {tab === "Access Requests" && <AccessRequestsTab />}
        {tab === "Investors" && <InvestorsTab />}
        {tab === "Vehicles" && <VehiclesTab />}
        {tab === "Documents" && <DocumentsTab />}
        {tab === "Statements" && <StatementsTab />}
        {tab === "Questions" && <QuestionsTab />}
        {tab === "Gates & Settings" && <SettingsTab />}
        {tab === "Audit" && <AuditTab />}
      </main>
      <Copilot audience="admin" />
    </div>
  );
}

const Card = ({ label, value, testid }) => (
  <div className="card-line rounded-xl bg-white p-5" data-testid={testid}>
    <p className="text-[11px] font-bold uppercase tracking-wider text-muted">{label}</p>
    <p className="mt-2 font-display text-2xl font-extrabold text-offwhite">{value}</p>
  </div>
);

function OverviewTab() {
  const [o, setO] = useState(null);
  useEffect(() => { api.get("/admin/investor/overview").then(({ data }) => setO(data)).catch(() => toast.error("Could not load overview")); }, []);
  if (!o) return <p className="py-16 text-center"><Loader2 className="mx-auto animate-spin text-red-700" /></p>;
  return (
    <div className="space-y-6" data-testid="ia-overview">
      <div className="flex flex-wrap gap-3">
        <GateBadge label="Legal Launch Gate" value={o.legal_launch_gate} testid="ia-gate-legal" />
        <GateBadge label="Payments Gate" value={o.payments_gate} testid="ia-gate-payments" />
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card label="Access requests" value={o.total_access_requests} testid="ia-total-requests" />
        <Card label="Awaiting review" value={o.requests_by_status?.new || 0} testid="ia-new-requests" />
        <Card label="Prospective investors" value={o.investors_by_stage?.prospective || 0} testid="ia-prospective" />
        <Card label="Approved investors" value={o.investors_by_stage?.approved || 0} testid="ia-approved" />
        <Card label="Active non-binding interests" value={o.active_interests} testid="ia-interests" />
        <Card label="Proposed amount (non-binding)" value={money(o.proposed_amount_total)} testid="ia-proposed-total" />
        <Card label="Statements awaiting approval" value={o.statements_awaiting_approval} testid="ia-stmt-pending" />
        <Card label="Distributions awaiting approval" value={o.distributions_awaiting_approval} testid="ia-dist-pending" />
        <Card label="Open investor questions" value={o.open_questions} testid="ia-open-questions" />
        <Card label="Missing diligence items" value={o.missing_diligence_items} testid="ia-missing-diligence" />
      </div>
      <div className="card-line rounded-xl bg-white p-6">
        <h2 className="font-display text-base font-extrabold text-offwhite">Vehicle funding & acquisition status</h2>
        <div className="mt-4 space-y-2">
          {o.vehicle_funding_status.map((v) => (
            <div key={v.vehicle_id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-black/[0.08] p-3 text-sm" data-testid={`ia-vehicle-${v.vehicle_id}`}>
              <span className="font-bold text-offwhite">{v.name}</span>
              <span className="text-xs text-muted">Proposed (non-binding): {money(v.proposed_amount)}</span>
              <Badge value={v.availability} testid={`ia-vehicle-avail-${v.vehicle_id}`} />
            </div>
          ))}
        </div>
      </div>
      {o.expirations_next_45_days.length > 0 && (
        <div className="rounded-xl border border-yellow-500/40 bg-yellow-400/10 p-5" data-testid="ia-expirations">
          <p className="font-display text-sm font-bold text-yellow-800">Expiring within 45 days</p>
          {o.expirations_next_45_days.map((e, i) => <p key={i} className="mt-1 text-xs text-yellow-800">{e.vehicle} — {e.kind} expires {e.date}</p>)}
        </div>
      )}
    </div>
  );
}

function AccessRequestsTab() {
  const [reqs, setReqs] = useState(null);
  const load = useCallback(() => api.get("/admin/investor/access-requests").then(({ data }) => setReqs(data)).catch(() => toast.error("Load failed")), []);
  useEffect(() => { load(); }, [load]);
  const review = async (id, status) => {
    try { await api.patch(`/admin/investor/access-requests/${id}`, { status }); toast.success(`Request ${status}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };
  if (!reqs) return <p className="py-16 text-center"><Loader2 className="mx-auto animate-spin text-red-700" /></p>;
  return (
    <div className="space-y-3" data-testid="ia-requests">
      {!reqs.length && <p className="card-line rounded-xl bg-white p-10 text-center text-sm text-muted">No access requests yet.</p>}
      {reqs.map((r) => (
        <div key={r.request_id} className="card-line rounded-xl bg-white p-5" data-testid={`ia-request-${r.request_id}`}>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <div className="min-w-0 flex-1">
              <p className="font-display text-sm font-extrabold text-offwhite">{r.first_name} {r.last_name} <span className="ml-2 font-mono text-[10px] font-normal text-muted">{r.request_id}</span></p>
              <p className="mt-0.5 text-xs text-muted">{r.email} · {r.phone} · {r.city_state}</p>
              <p className="mt-1 text-xs text-muted">{r.investor_type}{r.entity_name ? ` (${r.entity_name})` : ""} · Interest: {r.interest_range} · Tier: {r.preferred_tier || "—"} · Accredited: {r.accredited_status}</p>
              {r.vehicles_of_interest?.length > 0 && <p className="mt-1 text-[11px] text-muted">Vehicles: {r.vehicles_of_interest.join(", ")}</p>}
              {r.questions && <p className="mt-1 text-[11px] italic text-muted">"{r.questions}"</p>}
            </div>
            <Badge value={r.status} testid={`ia-request-status-${r.request_id}`} />
            <div className="flex gap-2">
              {r.status !== "approved" && <button onClick={() => review(r.request_id, "approved")} className="rounded-full bg-emerald-600 px-4 py-1.5 text-xs font-bold text-white hover:bg-emerald-500" data-testid={`ia-approve-${r.request_id}`}>Approve</button>}
              {r.status !== "declined" && <button onClick={() => review(r.request_id, "declined")} className="rounded-full border border-red-500/40 px-4 py-1.5 text-xs font-bold text-red-600 hover:bg-red-500/10" data-testid={`ia-decline-${r.request_id}`}>Decline</button>}
              {r.status !== "reviewing" && r.status !== "approved" && <button onClick={() => review(r.request_id, "reviewing")} className="rounded-full border border-black/15 px-4 py-1.5 text-xs font-bold text-muted" data-testid={`ia-reviewing-${r.request_id}`}>Mark reviewing</button>}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function InvestorsTab() {
  const [investors, setInvestors] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const load = useCallback(() => {
    api.get("/admin/investor/investors").then(({ data }) => setInvestors(data)).catch(() => toast.error("Load failed"));
    api.get("/admin/investor/vehicles").then(({ data }) => setVehicles(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const setStage = async (user_id, stage) => {
    try { await api.patch(`/admin/investor/investors/${user_id}`, { stage }); toast.success(`Investor is now ${stage}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };
  const assign = async (user_id, current, vehicle_id, tier_pct) => {
    const assigned_vehicles = [...current.filter((a) => a.vehicle_id !== vehicle_id), { vehicle_id, tier_pct: Number(tier_pct) }];
    try { await api.patch(`/admin/investor/investors/${user_id}`, { assigned_vehicles }); toast.success("Vehicle assigned"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Assign failed"); }
  };
  const unassign = async (user_id, current, vehicle_id) => {
    try { await api.patch(`/admin/investor/investors/${user_id}`, { assigned_vehicles: current.filter((a) => a.vehicle_id !== vehicle_id) }); toast.success("Assignment removed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  if (!investors) return <p className="py-16 text-center"><Loader2 className="mx-auto animate-spin text-red-700" /></p>;
  return (
    <div className="space-y-3" data-testid="ia-investors">
      {!investors.length && <p className="card-line rounded-xl bg-white p-10 text-center text-sm text-muted">No investor profiles yet.</p>}
      {investors.map((p) => (
        <div key={p.profile_id} className="card-line rounded-xl bg-white p-5" data-testid={`ia-investor-${p.user_id}`}>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <div className="min-w-0 flex-1">
              <p className="font-display text-sm font-extrabold text-offwhite">{p.name || p.email}</p>
              <p className="text-xs text-muted">{p.email} · {p.investor_type} · Accredited: {p.accredited_status} · {p.interests} active interests</p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {(p.assigned_vehicles || []).map((a) => (
                  <span key={a.vehicle_id} className="inline-flex items-center gap-1.5 rounded-full border border-red-700/30 bg-red-500/[0.06] px-2.5 py-0.5 text-[10px] font-bold text-red-700" data-testid={`ia-assigned-${p.user_id}-${a.vehicle_id}`}>
                    {a.vehicle_name} · {a.tier_pct}%
                    <button onClick={() => unassign(p.user_id, p.assigned_vehicles, a.vehicle_id)} className="hover:text-red-900" data-testid={`ia-unassign-${p.user_id}-${a.vehicle_id}`}>✕</button>
                  </span>
                ))}
              </div>
            </div>
            <Badge value={p.stage === "approved" ? "approved" : "reviewing"} testid={`ia-stage-${p.user_id}`} />
            <div className="flex flex-wrap items-center gap-2">
              <select defaultValue="" onChange={(e) => { const [vid, tier] = e.target.value.split("|"); if (vid) assign(p.user_id, p.assigned_vehicles || [], vid, tier); e.target.value = ""; }}
                className="field !w-auto !py-1.5 !text-xs" data-testid={`ia-assign-select-${p.user_id}`}>
                <option value="">Assign vehicle…</option>
                {vehicles.flatMap((v) => v.tiers.map((t) => <option key={`${v.vehicle_id}|${t.funding_pct}`} value={`${v.vehicle_id}|${t.funding_pct}`}>{v.name} — {t.funding_pct}%</option>))}
              </select>
              {p.stage !== "approved"
                ? <button onClick={() => setStage(p.user_id, "approved")} className="rounded-full bg-emerald-600 px-4 py-1.5 text-xs font-bold text-white hover:bg-emerald-500" data-testid={`ia-approve-investor-${p.user_id}`}>Approve investor</button>
                : <button onClick={() => setStage(p.user_id, "prospective")} className="rounded-full border border-black/15 px-4 py-1.5 text-xs font-bold text-muted" data-testid={`ia-demote-${p.user_id}`}>Move to prospective</button>}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function VehiclesTab() {
  const [vehicles, setVehicles] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api.get("/admin/investor/vehicles").then(({ data }) => setVehicles(data)).catch(() => toast.error("Load failed")), []);
  useEffect(() => { load(); }, [load]);

  const save = async (vehicle_id, updates) => {
    try { await api.put(`/admin/investor/vehicles/${vehicle_id}`, updates); toast.success("Vehicle updated"); setEdit(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };
  const toggleMilestone = async (vehicle_id, gate, done) => {
    try { await api.put(`/admin/investor/vehicles/${vehicle_id}/milestones/${gate}`, { done }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Milestone update failed"); }
  };
  const saveInsurance = async (vehicle_id, insurance) => {
    try { await api.put(`/admin/investor/vehicles/${vehicle_id}/insurance`, insurance); toast.success("Insurance updated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Insurance update failed"); }
  };

  if (!vehicles) return <p className="py-16 text-center"><Loader2 className="mx-auto animate-spin text-red-700" /></p>;
  return (
    <div className="space-y-4" data-testid="ia-vehicles">
      {vehicles.map((v) => (
        <div key={v.vehicle_id} className="card-line rounded-xl bg-white p-5" data-testid={`ia-veh-${v.vehicle_id}`}>
          <div className="flex flex-wrap items-center gap-3">
            <div className="min-w-0 flex-1">
              <p className="font-display text-sm font-extrabold text-offwhite">{v.name} <span className="ml-2 font-mono text-[10px] font-normal text-muted">{v.internal_vehicle_id}</span></p>
              <p className="text-xs text-muted">{money(v.acquisition_cost)} · {v.status}</p>
            </div>
            <Badge value={v.availability} testid={`ia-veh-avail-${v.vehicle_id}`} />
            <button onClick={() => setEdit(edit === v.vehicle_id ? null : v.vehicle_id)} className="btn-ghost !px-4 !py-1.5 !text-xs" data-testid={`ia-veh-edit-${v.vehicle_id}`}>
              {edit === v.vehicle_id ? "Close" : "Manage"}
            </button>
          </div>
          {edit === v.vehicle_id && (
            <div className="mt-5 space-y-5 border-t border-black/[0.06] pt-5">
              <div className="grid gap-4 sm:grid-cols-3">
                {[["availability", "Availability", ["open", "waitlist", "closed"]],
                  ["diligence_status", "Diligence status", null],
                  ["proposed_location_status", "Location status", null],
                  ["host_operator_status", "Host operator status", null],
                  ["insurance_status", "Insurance status", null],
                  ["permit_status", "Permit status", null],
                  ["purchase_order_status", "Purchase order status", null]].map(([k, label, opts]) => (
                  <div key={k}>
                    <label className="field-label">{label}</label>
                    {opts ? (
                      <select className="field !py-2 !text-xs" defaultValue={v[k]} onChange={(e) => save(v.vehicle_id, { [k]: e.target.value })} data-testid={`ia-veh-${k}-${v.vehicle_id}`}>
                        {opts.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    ) : (
                      <input className="field !py-2 !text-xs" defaultValue={v[k] || ""} onBlur={(e) => e.target.value !== (v[k] || "") && save(v.vehicle_id, { [k]: e.target.value })} data-testid={`ia-veh-${k}-${v.vehicle_id}`} />
                    )}
                  </div>
                ))}
              </div>
              <div>
                <p className="field-label">Milestones</p>
                <div className="mt-2 grid gap-1.5 sm:grid-cols-2">
                  {v.milestones.map((m) => (
                    <label key={m.gate} className="flex cursor-pointer items-center gap-2 text-xs text-muted" data-testid={`ia-milestone-${v.vehicle_id}-${m.gate}`}>
                      <input type="checkbox" className="h-3.5 w-3.5 accent-red-700" checked={m.done} onChange={(e) => toggleMilestone(v.vehicle_id, m.gate, e.target.checked)} />
                      {m.label} {m.date && <span className="font-mono text-[10px]">({m.date})</span>}
                    </label>
                  ))}
                </div>
              </div>
              <InsuranceForm v={v} onSave={(ins) => saveInsurance(v.vehicle_id, ins)} />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function InsuranceForm({ v, onSave }) {
  const [ins, setIns] = useState({ ...(v.insurance || {}) });
  const set = (k, val) => setIns((p) => ({ ...p, [k]: val }));
  const Num = ({ k, label }) => (
    <div>
      <label className="field-label">{label}</label>
      <input type="number" min="0" className="field !py-2 !text-xs" value={ins[k] ?? ""} onChange={(e) => set(k, e.target.value === "" ? null : Number(e.target.value))} data-testid={`ia-ins-${k}`} />
    </div>
  );
  const Txt = ({ k, label }) => (
    <div>
      <label className="field-label">{label}</label>
      <input className="field !py-2 !text-xs" value={ins[k] ?? ""} onChange={(e) => set(k, e.target.value)} data-testid={`ia-ins-${k}`} />
    </div>
  );
  return (
    <div className="rounded-xl border border-black/[0.08] bg-surface/40 p-4">
      <p className="field-label">Insurance tracking</p>
      <div className="mt-3 grid gap-3 sm:grid-cols-3">
        <Txt k="carrier" label="Carrier" />
        <Txt k="policy_number" label="Policy number" />
        <Txt k="endorsement_status" label="Endorsement status" />
        <Txt k="effective_date" label="Effective date (YYYY-MM-DD)" />
        <Txt k="expiration_date" label="Expiration date (YYYY-MM-DD)" />
        <Txt k="claim_status" label="Claim status" />
        <Num k="liability_limit" label="Liability limit ($)" />
        <Num k="physical_damage_limit" label="Physical damage limit ($)" />
        <Num k="deductible" label="Deductible ($)" />
        <Num k="partner_deductible_contribution" label="Partner deductible contribution ($)" />
      </div>
      <p className="mt-2 text-[10px] text-muted">Partner contributes 45% of any deductible, capped at $2,500; Xtreme Exotics covers the balance.</p>
      <button onClick={() => onSave(ins)} className="btn-ghost mt-3 !px-4 !py-1.5 !text-xs" data-testid={`ia-ins-save-${v.vehicle_id}`}>Save insurance</button>
    </div>
  );
}

const DOC_CATEGORIES = [
  "Revised Investor Proposal", "Definitive Investor Agreement", "Vehicle Purchase Order", "Sources and Uses",
  "Host Fleet Partner Agreement", "Location Authorization", "Insurance Binder", "Liability Coverage",
  "Physical Damage Coverage", "Additional Insured Endorsement", "Permits", "Booking Procedures",
  "Maintenance Records", "Vehicle Tracking Records", "Tax Documents", "Notices",
];

function DocumentsTab() {
  const [docs, setDocs] = useState(null);
  const [form, setForm] = useState({ title: "", category: DOC_CATEGORIES[0], body: "", url: "", visibility: "approved", requires_signature: false });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/admin/investor/documents").then(({ data }) => setDocs(data)).catch(() => toast.error("Load failed")), []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setBusy(true);
    try {
      let file_path = "";
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        const { data: up } = await api.post("/admin/investor/documents/upload", fd);
        file_path = up.storage_path;
      }
      await api.post("/admin/investor/documents", { ...form, file_path });
      toast.success("Document published");
      setForm({ title: "", category: DOC_CATEGORIES[0], body: "", url: "", visibility: "approved", requires_signature: false });
      setFile(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
    finally { setBusy(false); }
  };
  const archive = async (id) => {
    try { await api.put(`/admin/investor/documents/${id}`, { status: "archived" }); toast.success("Archived"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Archive failed"); }
  };
  const showLog = async (id) => {
    try {
      const { data } = await api.get(`/admin/investor/documents/${id}/access-log`);
      toast.info(data.length ? data.map((l) => `${l.email} · ${new Date(l.created_at).toLocaleString()}`).join("\n") : "No views recorded", { duration: 6000 });
    } catch { toast.error("Could not load access log"); }
  };

  if (!docs) return <p className="py-16 text-center"><Loader2 className="mx-auto animate-spin text-red-700" /></p>;
  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_1.4fr]" data-testid="ia-documents">
      <div className="card-line h-fit rounded-xl bg-white p-5">
        <h2 className="font-display text-sm font-extrabold text-offwhite">Publish document</h2>
        <div className="mt-4 space-y-3">
          <input className="field !py-2.5 !text-sm" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="ia-doc-title" />
          <select className="field !py-2.5 !text-sm" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="ia-doc-category">
            {DOC_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </select>
          <textarea className="field min-h-[90px] !text-sm" placeholder="Content / summary" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="ia-doc-body" />
          <input className="field !py-2.5 !text-sm" placeholder="Secure file URL (optional)" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} data-testid="ia-doc-url" />
          <div>
            <label className="field-label">Attach file (PDF, image, CSV — max 25 MB)</label>
            <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.csv,.txt"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="block w-full text-xs text-muted file:mr-3 file:rounded-full file:border-0 file:bg-red-700 file:px-4 file:py-2 file:text-xs file:font-bold file:text-white hover:file:bg-red-600"
              data-testid="ia-doc-file" />
            {file && <p className="mt-1 text-[11px] text-muted" data-testid="ia-doc-file-name">{file.name} · {(file.size / 1024).toFixed(0)} KB</p>}
          </div>
          <select className="field !py-2.5 !text-sm" value={form.visibility} onChange={(e) => setForm({ ...form, visibility: e.target.value })} data-testid="ia-doc-visibility">
            <option value="prospective">Prospective investors & above</option>
            <option value="approved">Approved investors only</option>
          </select>
          <label className="flex cursor-pointer items-center gap-2.5 text-xs font-semibold text-offwhite">
            <input type="checkbox" className="h-4 w-4 accent-red-700" checked={form.requires_signature}
              onChange={(e) => setForm({ ...form, requires_signature: e.target.checked })} data-testid="ia-doc-requires-signature" />
            Requires investor e-signature
          </label>
          <button onClick={create} disabled={!form.title.trim() || busy} className="w-full rounded-full bg-red-700 px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-white hover:bg-red-600 disabled:opacity-50" data-testid="ia-doc-create-btn">
            {busy ? <Loader2 size={14} className="mx-auto animate-spin" /> : "Publish"}
          </button>
        </div>
      </div>
      <div className="space-y-3">
        {!docs.length && <p className="card-line rounded-xl bg-white p-10 text-center text-sm text-muted">No documents yet.</p>}
        {docs.map((d) => (
          <div key={d.document_id} className="card-line rounded-xl bg-white p-4" data-testid={`ia-doc-${d.document_id}`}>
            <div className="flex flex-wrap items-center gap-3">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold text-offwhite">{d.title} <span className="font-mono text-[10px] font-normal text-muted">v{d.version} · {d.visibility}</span></p>
                <p className="text-[11px] text-muted">{d.category} · updated {new Date(d.updated_at).toLocaleString()}
                  {d.requires_signature && <span className="ml-1 font-semibold text-red-700">· signature required ({(d.signatures || []).length} signed)</span>}
                </p>
              </div>
              <Badge value={d.status} testid={`ia-doc-status-${d.document_id}`} />
              {d.file_path && (
                <button onClick={async () => { try { await downloadBlob(`/investor/documents/${d.document_id}/file`); } catch { toast.error("Download failed"); } }}
                  className="text-muted hover:text-red-700" title="Download attached file" data-testid={`ia-doc-download-${d.document_id}`}>
                  <Download size={14} />
                </button>
              )}
              <button onClick={() => showLog(d.document_id)} className="text-[11px] font-semibold text-muted hover:text-red-700" data-testid={`ia-doc-log-${d.document_id}`}>Access log</button>
              {d.status === "active" && <button onClick={() => archive(d.document_id)} className="text-[11px] font-semibold text-red-600 hover:underline" data-testid={`ia-doc-archive-${d.document_id}`}>Archive</button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StatementsTab() {
  const [stmts, setStmts] = useState(null);
  const [dists, setDists] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [form, setForm] = useState({ vehicle_id: "", period_label: "", gross_revenue: "", refunds: 0, eligible_hours: "", maintenance_reserve: 0, other_reserves: 0 });
  const [expenses, setExpenses] = useState([{ label: "", amount: "" }]);

  const load = useCallback(() => {
    api.get("/admin/investor/statements").then(({ data }) => setStmts(data)).catch(() => toast.error("Load failed"));
    api.get("/admin/investor/distributions").then(({ data }) => setDists(data)).catch(() => {});
    api.get("/admin/investor/vehicles").then(({ data }) => setVehicles(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    try {
      await api.post("/admin/investor/statements", {
        ...form,
        gross_revenue: Number(form.gross_revenue) || 0, refunds: Number(form.refunds) || 0,
        eligible_hours: Number(form.eligible_hours) || 0,
        maintenance_reserve: Number(form.maintenance_reserve) || 0, other_reserves: Number(form.other_reserves) || 0,
        expenses: expenses.filter((e) => e.label.trim()).map((e) => ({ label: e.label, amount: Number(e.amount) || 0 })),
      });
      toast.success("Statement created (pending approval)");
      setForm({ vehicle_id: "", period_label: "", gross_revenue: "", refunds: 0, eligible_hours: "", maintenance_reserve: 0, other_reserves: 0 });
      setExpenses([{ label: "", amount: "" }]);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
  };
  const publish = async (id) => {
    try { await api.post(`/admin/investor/statements/${id}/publish`); toast.success("Statement published — distributions recorded"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Publish failed"); }
  };
  const setDist = async (id, status) => {
    try { await api.patch(`/admin/investor/distributions/${id}`, { status }); toast.success(`Distribution ${status}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  if (!stmts) return <p className="py-16 text-center"><Loader2 className="mx-auto animate-spin text-red-700" /></p>;
  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_1.4fr]" data-testid="ia-statements">
      <div className="card-line h-fit rounded-xl bg-white p-5">
        <h2 className="font-display text-sm font-extrabold text-offwhite">New vehicle statement</h2>
        <div className="mt-4 space-y-3">
          <select className="field !py-2.5 !text-sm" value={form.vehicle_id} onChange={(e) => setForm({ ...form, vehicle_id: e.target.value })} data-testid="ia-stmt-vehicle">
            <option value="">Vehicle…</option>
            {vehicles.map((v) => <option key={v.vehicle_id} value={v.vehicle_id}>{v.name}</option>)}
          </select>
          <input className="field !py-2.5 !text-sm" placeholder="Period (e.g. Week of Sep 28 – Oct 4, 2026)" value={form.period_label} onChange={(e) => setForm({ ...form, period_label: e.target.value })} data-testid="ia-stmt-period" />
          <div className="grid grid-cols-2 gap-3">
            <input type="number" min="0" className="field !py-2.5 !text-sm" placeholder="Gross revenue" value={form.gross_revenue} onChange={(e) => setForm({ ...form, gross_revenue: e.target.value })} data-testid="ia-stmt-gross" />
            <input type="number" min="0" className="field !py-2.5 !text-sm" placeholder="Refunds" value={form.refunds} onChange={(e) => setForm({ ...form, refunds: e.target.value })} data-testid="ia-stmt-refunds" />
            <input type="number" min="0" className="field !py-2.5 !text-sm" placeholder="Eligible hours" value={form.eligible_hours} onChange={(e) => setForm({ ...form, eligible_hours: e.target.value })} data-testid="ia-stmt-hours" />
            <input type="number" min="0" className="field !py-2.5 !text-sm" placeholder="Maintenance reserve" value={form.maintenance_reserve} onChange={(e) => setForm({ ...form, maintenance_reserve: e.target.value })} data-testid="ia-stmt-maint" />
          </div>
          <input type="number" min="0" className="field !py-2.5 !text-sm" placeholder="Other reserves" value={form.other_reserves} onChange={(e) => setForm({ ...form, other_reserves: e.target.value })} data-testid="ia-stmt-other" />
          <div>
            <p className="field-label">Itemized expenses</p>
            {expenses.map((ex, i) => (
              <div key={i} className="mt-2 grid grid-cols-[1fr_110px] gap-2">
                <input className="field !py-2 !text-xs" placeholder="Label" value={ex.label} onChange={(e) => setExpenses(expenses.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} data-testid={`ia-stmt-exp-label-${i}`} />
                <input type="number" min="0" className="field !py-2 !text-xs" placeholder="Amount" value={ex.amount} onChange={(e) => setExpenses(expenses.map((x, j) => (j === i ? { ...x, amount: e.target.value } : x)))} data-testid={`ia-stmt-exp-amount-${i}`} />
              </div>
            ))}
            <button onClick={() => setExpenses([...expenses, { label: "", amount: "" }])} className="mt-2 text-[11px] font-semibold text-red-700 hover:underline" data-testid="ia-stmt-add-expense">+ Add expense</button>
          </div>
          <button onClick={create} disabled={!form.vehicle_id || !form.period_label.trim()} className="w-full rounded-full bg-red-700 px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-white hover:bg-red-600 disabled:opacity-50" data-testid="ia-stmt-create-btn">Create statement</button>
        </div>
      </div>

      <div className="space-y-6">
        <div className="space-y-3">
          {!stmts.length && <p className="card-line rounded-xl bg-white p-10 text-center text-sm text-muted">No statements yet.</p>}
          {stmts.map((s) => (
            <div key={s.statement_id} className="card-line rounded-xl bg-white p-4" data-testid={`ia-stmt-${s.statement_id}`}>
              <div className="flex flex-wrap items-center gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold text-offwhite">{s.vehicle_name} — {s.period_label}</p>
                  <p className="text-[11px] text-muted">
                    Gross {money(s.gross_revenue)} · {s.eligible_hours}h · Partner {money(s.fleet_partner_payout)} ({s.partner_pct_applied}%) · DVP {money(s.defined_vehicle_profit)}
                  </p>
                </div>
                <Badge value={s.status} testid={`ia-stmt-status-${s.statement_id}`} />
                {s.status === "pending_approval" && (
                  <button onClick={() => publish(s.statement_id)} className="rounded-full bg-emerald-600 px-4 py-1.5 text-xs font-bold text-white hover:bg-emerald-500" data-testid={`ia-stmt-publish-${s.statement_id}`}>Approve & publish</button>
                )}
              </div>
            </div>
          ))}
        </div>
        <div className="card-line rounded-xl bg-white p-5">
          <h2 className="font-display text-sm font-extrabold text-offwhite">Distributions</h2>
          <div className="mt-3 space-y-2">
            {!dists.length && <p className="text-xs text-muted">None recorded.</p>}
            {dists.map((d) => (
              <div key={d.distribution_id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-black/[0.08] p-3" data-testid={`ia-dist-${d.distribution_id}`}>
                <p className="text-xs font-bold text-offwhite">{d.vehicle_name} · {d.period_label} · {money(d.amount)}</p>
                <div className="flex items-center gap-2">
                  <Badge value={d.status} testid={`ia-dist-status-${d.distribution_id}`} />
                  {d.status === "pending" && <button onClick={() => setDist(d.distribution_id, "approved")} className="text-[11px] font-bold text-emerald-700 hover:underline" data-testid={`ia-dist-approve-${d.distribution_id}`}>Approve</button>}
                  {d.status === "approved" && <button onClick={() => setDist(d.distribution_id, "paid")} className="text-[11px] font-bold text-emerald-700 hover:underline" data-testid={`ia-dist-paid-${d.distribution_id}`}>Mark paid</button>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function QuestionsTab() {
  const [qs, setQs] = useState(null);
  const [meetings, setMeetings] = useState([]);
  const [reply, setReply] = useState({});
  const load = useCallback(() => {
    api.get("/admin/investor/questions").then(({ data }) => setQs(data)).catch(() => toast.error("Load failed"));
    api.get("/admin/investor/meeting-requests").then(({ data }) => setMeetings(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  const send = async (id) => {
    try { await api.post(`/admin/investor/questions/${id}/reply`, { reply: reply[id] }); toast.success("Reply sent"); setReply({ ...reply, [id]: "" }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Reply failed"); }
  };
  if (!qs) return <p className="py-16 text-center"><Loader2 className="mx-auto animate-spin text-red-700" /></p>;
  return (
    <div className="grid gap-6 lg:grid-cols-2" data-testid="ia-questions">
      <div className="space-y-3">
        <h2 className="font-display text-base font-extrabold text-offwhite">Investor questions</h2>
        {!qs.length && <p className="card-line rounded-xl bg-white p-8 text-center text-sm text-muted">No questions.</p>}
        {qs.map((q) => (
          <div key={q.question_id} className="card-line rounded-xl bg-white p-4" data-testid={`ia-q-${q.question_id}`}>
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-bold text-offwhite">{q.subject}</p>
              <Badge value={q.status} testid={`ia-q-status-${q.question_id}`} />
            </div>
            <p className="mt-1 text-xs text-muted">{q.email} · {new Date(q.created_at).toLocaleString()}</p>
            <p className="mt-2 text-sm text-offwhite">{q.message}</p>
            {q.reply && <p className="mt-2 rounded bg-surface/70 p-2 text-xs"><b>Reply:</b> {q.reply}</p>}
            {q.status === "open" && (
              <div className="mt-3 flex gap-2">
                <input className="field !py-2 !text-xs" placeholder="Reply…" value={reply[q.question_id] || ""} onChange={(e) => setReply({ ...reply, [q.question_id]: e.target.value })} data-testid={`ia-q-reply-${q.question_id}`} />
                <button onClick={() => send(q.question_id)} disabled={!(reply[q.question_id] || "").trim()} className="btn-ghost !px-4 !py-2 !text-xs disabled:opacity-50" data-testid={`ia-q-send-${q.question_id}`}>Send</button>
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="space-y-3">
        <h2 className="font-display text-base font-extrabold text-offwhite">Meeting requests</h2>
        {!meetings.length && <p className="card-line rounded-xl bg-white p-8 text-center text-sm text-muted">No meeting requests.</p>}
        {meetings.map((m) => (
          <div key={m.meeting_id} className="card-line rounded-xl bg-white p-4" data-testid={`ia-meeting-${m.meeting_id}`}>
            <p className="text-sm font-bold text-offwhite">{m.topic}</p>
            <p className="mt-1 text-xs text-muted">{m.email} · {m.preferred_times}</p>
            {m.note && <p className="mt-1 text-xs text-muted">{m.note}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

function SettingsTab() {
  const [s, setS] = useState(null);
  const load = useCallback(() => api.get("/admin/investor/settings").then(({ data }) => setS(data)).catch(() => toast.error("Load failed")), []);
  useEffect(() => { load(); }, [load]);
  const patch = async (updates) => {
    try { await api.patch("/admin/investor/settings", updates); toast.success("Settings saved"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  if (!s) return <p className="py-16 text-center"><Loader2 className="mx-auto animate-spin text-red-700" /></p>;
  return (
    <div className="grid gap-6 lg:grid-cols-2" data-testid="ia-settings">
      <div className="card-line rounded-xl bg-white p-6">
        <h2 className="font-display text-base font-extrabold text-offwhite">Launch gates <span className="ml-2 font-mono text-[10px] font-normal uppercase tracking-widest text-muted">Super Admin only</span></h2>
        <div className="mt-4 space-y-4">
          <div className="flex items-center justify-between gap-3 rounded-lg border border-black/[0.08] p-4">
            <div>
              <p className="text-sm font-bold text-offwhite">Legal Launch Gate</p>
              <p className="text-[11px] text-muted">When ON, no definitive agreements or live funding are permitted.</p>
            </div>
            <div className="flex items-center gap-2">
              <GateBadge label="" value={s.legal_launch_gate} testid="ia-settings-legal" />
              <button onClick={() => patch({ legal_launch_gate: s.legal_launch_gate === "ON" ? "OFF" : "ON" })}
                className="rounded-full border border-red-700/40 px-4 py-1.5 text-xs font-bold text-red-700 hover:bg-red-500/10" data-testid="ia-toggle-legal">
                Turn {s.legal_launch_gate === "ON" ? "OFF" : "ON"}
              </button>
            </div>
          </div>
          <div className="flex items-center justify-between gap-3 rounded-lg border border-black/[0.08] p-4">
            <div>
              <p className="text-sm font-bold text-offwhite">Payments Gate</p>
              <p className="text-[11px] text-muted">OFF closes funding · TEST shows simulated funding only · LIVE is disabled.</p>
            </div>
            <div className="flex items-center gap-2">
              <GateBadge label="" value={s.payments_gate} testid="ia-settings-payments" />
              <select value={s.payments_gate} onChange={(e) => patch({ payments_gate: e.target.value })} className="field !w-auto !py-1.5 !text-xs" data-testid="ia-payments-select">
                {["OFF", "TEST", "LIVE"].map((g) => <option key={g}>{g}</option>)}
              </select>
            </div>
          </div>
          <div className="flex items-center justify-between gap-3 rounded-lg border border-black/[0.08] p-4">
            <div>
              <p className="text-sm font-bold text-offwhite">Weekly investor digest</p>
              <p className="text-[11px] text-muted">Mondays 9am ET — emails investors when new statements or documents publish.</p>
            </div>
            <button onClick={() => patch({ weekly_digest_enabled: !(s.weekly_digest_enabled ?? true) })}
              className={`rounded-full border px-4 py-1.5 text-xs font-bold ${(s.weekly_digest_enabled ?? true) ? "border-emerald-600/40 text-emerald-700" : "border-black/20 text-muted"}`}
              data-testid="ia-digest-toggle">
              {(s.weekly_digest_enabled ?? true) ? "Enabled" : "Disabled"}
            </button>
          </div>
          <div className="flex items-center justify-between gap-3 rounded-lg border border-black/[0.08] p-4">
            <div>
              <p className="text-sm font-bold text-offwhite">Weekly ops digest (you)</p>
              <p className="text-[11px] text-muted">Mondays 8am ET — fleet revenue, settlements and investor activity summary.</p>
            </div>
            <button onClick={() => patch({ ops_digest_enabled: !(s.ops_digest_enabled ?? true) })}
              className={`rounded-full border px-4 py-1.5 text-xs font-bold ${(s.ops_digest_enabled ?? true) ? "border-emerald-600/40 text-emerald-700" : "border-black/20 text-muted"}`}
              data-testid="ia-ops-digest-toggle">
              {(s.ops_digest_enabled ?? true) ? "Enabled" : "Disabled"}
            </button>
          </div>
          <div className="flex items-center justify-between gap-3 rounded-lg border border-black/[0.08] p-4">
            <div>
              <p className="text-sm font-bold text-offwhite">Opportunity visibility</p>
              <p className="text-[11px] text-muted">Public shows vehicle cards on the investor landing page.</p>
            </div>
            <select value={s.opportunity_visibility || "public"} onChange={(e) => patch({ opportunity_visibility: e.target.value })} className="field !w-auto !py-1.5 !text-xs" data-testid="ia-visibility-select">
              <option value="public">Public</option>
              <option value="request_only">After approval only</option>
            </select>
          </div>
        </div>
      </div>
      <div className="card-line rounded-xl bg-white p-6">
        <h2 className="font-display text-base font-extrabold text-offwhite">Disclosures & eligibility</h2>
        <div className="mt-4 space-y-4">
          <div>
            <label className="field-label">Legal disclosures</label>
            <textarea className="field min-h-[100px] !text-xs" defaultValue={s.legal_disclosures} onBlur={(e) => e.target.value !== s.legal_disclosures && patch({ legal_disclosures: e.target.value })} data-testid="ia-disclosures" />
          </div>
          <div>
            <label className="field-label">Investor eligibility rules</label>
            <textarea className="field min-h-[100px] !text-xs" defaultValue={s.eligibility_rules} onBlur={(e) => e.target.value !== s.eligibility_rules && patch({ eligibility_rules: e.target.value })} data-testid="ia-eligibility" />
          </div>
          <div>
            <label className="field-label">Investor contact email</label>
            <input className="field !text-xs" defaultValue={s.contact_email} onBlur={(e) => e.target.value !== s.contact_email && patch({ contact_email: e.target.value })} data-testid="ia-contact-email" />
          </div>
        </div>
      </div>
    </div>
  );
}

function AuditTab() {
  const [events, setEvents] = useState(null);
  useEffect(() => { api.get("/admin/investor/audit").then(({ data }) => setEvents(data)).catch(() => toast.error("Load failed")); }, []);
  if (!events) return <p className="py-16 text-center"><Loader2 className="mx-auto animate-spin text-red-700" /></p>;
  return (
    <div className="card-line overflow-hidden rounded-xl bg-white" data-testid="ia-audit">
      <table className="w-full text-left text-xs">
        <thead className="border-b border-black/[0.08] bg-surface/50 font-mono text-[10px] uppercase tracking-widest text-muted">
          <tr><th className="px-4 py-3">When</th><th className="px-4 py-3">Actor</th><th className="px-4 py-3">Action</th><th className="px-4 py-3">Entity</th><th className="px-4 py-3">Details</th></tr>
        </thead>
        <tbody>
          {events.map((e) => (
            <tr key={e.event_id} className="border-b border-black/[0.04]" data-testid={`ia-audit-${e.event_id}`}>
              <td className="whitespace-nowrap px-4 py-2.5 font-mono text-muted">{new Date(e.created_at).toLocaleString()}</td>
              <td className="px-4 py-2.5 text-muted">{e.actor}</td>
              <td className="px-4 py-2.5 font-semibold text-offwhite">{e.action.replace(/_/g, " ")}</td>
              <td className="px-4 py-2.5 font-mono text-muted">{e.entity}:{e.entity_id}</td>
              <td className="max-w-[260px] truncate px-4 py-2.5 text-muted">{JSON.stringify(e.meta)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
